/**
 * User Activity Tracking Service
 * Sends events to Django backend via axios, which writes to S3 for Snowflake ingestion.
 * Only active in production.
 */
import axios from 'axios'

const TRACKING_ENABLED = import.meta.env.VITE_ENV === 'production'

class TrackingService {
  constructor() {
    this.sessionId = this.getOrCreateSessionId()
    this.queue = []
    this.flushInterval = 5000 // 5 seconds
    this.maxQueueSize = 50
    this.userId = null
    this.tenantSlug = null
    this.entityId = null
    this.flushTimer = null
    this.disabled = false // set true on auth failure to stop retrying
  }

  getOrCreateSessionId() {
    let sessionId = sessionStorage.getItem('tracking_session_id')
    if (!sessionId) {
      sessionId = crypto.randomUUID()
      sessionStorage.setItem('tracking_session_id', sessionId)
    }
    return sessionId
  }

  /**
   * Call after login to enrich events with user context
   */
  setUserContext({ userId, tenantSlug, entityId }) {
    this.userId = userId
    this.tenantSlug = userId ? tenantSlug : null
    this.entityId = userId ? entityId : null
    // Re-enable tracking on login
    if (userId) this.disabled = false
  }

  /**
   * Track an event
   */
  track(eventType, eventData = {}, options = {}) {
    const event = {
      ID: crypto.randomUUID(),
      SESSION_ID: this.sessionId,
      EVENT_TYPE: eventType,
      EVENT_DATA: eventData,
      USER_ID: this.userId,
      TENANT_SLUG: this.tenantSlug,
      ENTITY_ID: this.entityId,
      PAGE_URL: window.location.href,
      PAGE_ROUTE: window.__currentRoute || null,
      USER_AGENT: navigator.userAgent,
      VIEWPORT_WIDTH: window.innerWidth,
      VIEWPORT_HEIGHT: window.innerHeight,
      TIMEZONE: Intl.DateTimeFormat().resolvedOptions().timeZone,
      CLIENT_TIMESTAMP: new Date().toISOString(),
      CREATED_AT: new Date().toISOString(),
      REQUEST_PAYLOAD: options.requestPayload ?? null,
      RESPONSE_BODY: options.responseBody ?? null,
      REQUEST_HEADERS: options.requestHeaders ?? null,
      RESPONSE_HEADERS: options.responseHeaders ?? null,
    }

    this.queue.push(event)

    if (this.queue.length >= this.maxQueueSize) {
      this.flush()
    }
  }

  safeSerialize(data) {
    if (data === null || data === undefined) return null
    try {
      JSON.stringify(data)
      return data
    } catch {
      return '[unserializable]'
    }
  }

  async flush() {
    if (!TRACKING_ENABLED || this.queue.length === 0 || !this.tenantSlug) return
    if (this.disabled) return

    const events = [...this.queue]
    this.queue = []

    axios.post('api/tracking/events/', events).catch((error) => {
      // On 401/403, stop all future flushes (session expired)
      if (error.response?.status === 401 || error.response?.status === 403) {
        this.disabled = true
        return
      }
      // For other errors, re-queue events for retry
      if (this.queue.length < 500) {
        this.queue.push(...events)
      }
      console.warn('Tracking flush failed:', error)
    })
  }

  start() {
    this.flushTimer = setInterval(() => this.flush(), this.flushInterval)

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush()
      }
    })
    window.addEventListener('pagehide', () => this.flush())

    this.track('session_start', {
      referrer: document.referrer,
      screenWidth: screen.width,
      screenHeight: screen.height,
      language: navigator.language,
    })

    document.addEventListener('click', (e) => {
      const target = e.target.closest('button, a, [role="button"], [data-track]')
      if (target) {
        this.track('click', {
          tagName: target.tagName,
          text: (target.innerText || '').slice(0, 100),
          href: target.href || null,
          dataTrack: target.dataset?.track || null,
          className: target.className || null,
        })
      }
    }, { capture: true })

    window.addEventListener('error', (e) => {
      this.track('error', {
        message: e.message,
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
      })
    })

    window.addEventListener('unhandledrejection', (e) => {
      this.track('error', {
        message: e.reason?.message || String(e.reason),
        type: 'unhandledrejection',
      })
    })
  }

  extractHeaders(headers) {
    if (!headers) return null
    if (typeof headers.toJSON === 'function') {
      return headers.toJSON()
    }
    try {
      return Object.fromEntries(
        Object.entries(headers).filter(([, v]) => typeof v !== 'function')
      )
    } catch {
      return null
    }
  }

  setupAxiosTracking() {
    axios.interceptors.request.use((config) => {
      config._trackingStartTime = Date.now()
      config._trackingRequestData = config.data
      config._trackingRequestHeaders = this.extractHeaders(config.headers)
      return config
    })

    axios.interceptors.response.use(
      (response) => {
        if (response.config.url?.includes('api/tracking/')) return response

        this.track('api_call', {
          url: response.config.url,
          method: response.config.method?.toUpperCase(),
          status: response.status,
          duration: Date.now() - (response.config._trackingStartTime || Date.now()),
        }, {
          requestPayload: this.safeSerialize(response.config._trackingRequestData),
          requestHeaders: response.config._trackingRequestHeaders,
          responseHeaders: this.extractHeaders(response.headers),
        })
        return response
      },
      (error) => {
        if (error.config?.url?.includes('api/tracking/')) return Promise.reject(error)

        this.track('api_error', {
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
          status: error.response?.status,
          message: error.message,
          duration: Date.now() - (error.config?._trackingStartTime || Date.now()),
        }, {
          requestPayload: this.safeSerialize(error.config?._trackingRequestData),
          requestHeaders: error.config?._trackingRequestHeaders,
          responseHeaders: this.extractHeaders(error.response?.headers),
        })
        return Promise.reject(error)
      }
    )
  }
}

export const tracking = new TrackingService()
export default tracking
